/**
 * Auto-format short numeric input → HH:MM.
 *
 * UX-rule: typing "0800" or "800" or "8" should land as "08:00" without making
 * the user reach for the colon. This mirrors how spreadsheet operators enter
 * times. Non-time content (e.g. status code "K", "ZA", "Urlaub") passes through
 * unchanged — the validator handles those.
 *
 * Conventions:
 *   ""     → ""           (empty stays empty — different from "0:00")
 *   "8"    → "08:00"      (1-digit = hour-only)
 *   "17"   → "17:00"      (2-digit = hour-only)
 *   "830"  → "08:30"      (3-digit = HMM, hour padded)
 *   "0800" → "08:00"      (4-digit = HHMM)
 *   "1730" → "17:30"      (4-digit)
 *   "8:30" → "08:30"      (already-colon, normalize hour pad)
 *   "K"    → "K"          (passthrough — validator's job)
 *   "8:9"  → "08:09"      (already-colon, pad both)
 *   "25:00"→ "25:00"      (out-of-range — validator catches as klasse-5b)
 */
export function formatTimeInput(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";

  // Already-with-colon (or dot/comma): normalize the two halves to padded form.
  // We accept "8:30", "8.30", "8,30" to match how some users type on numeric pads.
  const colonMatch = /^(\d{1,2})[:.,](\d{1,2})$/.exec(trimmed);
  if (colonMatch) {
    const h = colonMatch[1]!.padStart(2, "0");
    const m = colonMatch[2]!.padStart(2, "0");
    return `${h}:${m}`;
  }

  // Pure-digit-only path. Anything else (letters, punctuation other than
  // colon/dot/comma) passes through — likely a status code or partial input.
  if (!/^\d{1,4}$/.test(trimmed)) return trimmed;

  if (trimmed.length <= 2) {
    // "8" or "17" → hour-only
    const h = trimmed.padStart(2, "0");
    return `${h}:00`;
  }
  if (trimmed.length === 3) {
    // "830" → "08:30"  (HMM convention; pad hour, take last 2 as minutes)
    const h = `0${trimmed[0]}`;
    const m = trimmed.slice(1);
    return `${h}:${m}`;
  }
  // length 4: "1730" → "17:30"
  const h = trimmed.slice(0, 2);
  const m = trimmed.slice(2);
  return `${h}:${m}`;
}

/** True for the four time-fields. Status field "symb" stays raw. */
export function isTimeField(field: string): boolean {
  return field === "von1" || field === "bis1" || field === "von2" || field === "bis2";
}
